// A/B experiment registry. Single source of truth for every live test.
//
// To run a new test:
//   1. Add an entry here (flag key + variant keys, "control" first) with the
//      admin metadata below — it powers the experiments page (/admin/experiments),
//      so write `title`/`hypothesis`/`variantMeta` for a marketeer, not a dev.
//   2. For a COPY test: add the variant copy under `ab.<flag>` in
//      src/messages/{nl,en}.json for the section you are testing (the default key
//      stays the SEO control), set `kind: "copy"` + `section`/`keys`/`keyLabels`,
//      and in the component use `useCopy(namespace, flag)` + `tx(key)`.
//      For a PRESENCE test (show/hide a marketing element): set `kind: "presence"`
//      and read the variant key directly (useFeatureFlagVariantKey / the `?ab=`
//      override), no message copy.
//   3. In PostHog, create a multivariate feature flag with the same key and
//      variants, then wrap it in an Experiment (goal metric below).
//
// The `flag` string MUST match the PostHog feature-flag key exactly.

/** Plain-language description of a single variant, shown in the admin. */
export type VariantMeta = {
  /** Short human label, e.g. "Waarde-framing". */
  label: string;
  /** What this variant changes and why, for a non-developer. */
  description: string;
};

export type Experiment = {
  /** PostHog feature-flag key. */
  flag: string;
  /** Variant keys. First entry is the control (= the default / current site). */
  variants: [string, ...string[]];
  /** One-line summary of what is being tested (also used by the preview tool). */
  note?: string;
  // ---- Admin overview metadata (/admin/experiments). Optional so the preview
  // tool and the `?ab=` override keep working with only flag+variants, but fill
  // them in for every real test so the overview stays useful. ----
  /** Human title, e.g. "Hero-koptekst & CTA". */
  title?: string;
  /** What we expect to learn — the hypothesis behind the test. */
  hypothesis?: string;
  /** PostHog goal metric (an event name from events.ts), e.g. "cta_click". */
  goal?: string;
  /** Where on the site the test runs, in plain language. */
  location?: string;
  /** "copy" swaps message text; "presence" shows/hides a marketing element. */
  kind?: "copy" | "presence";
  /** Copy tests only: the next-intl namespace holding `ab.<flag>.<variant>`. */
  section?: string;
  /** Copy tests only: the message keys within that section under test. */
  keys?: string[];
  /** Copy tests only: friendly labels for those keys (raw key as fallback). */
  keyLabels?: Record<string, string>;
  /** Per-variant metadata, keyed by variant key (cover every variant). */
  variantMeta?: Record<string, VariantMeta>;
};

export const experiments = {
  "hero-delay-banner": {
    flag: "hero-delay-banner",
    variants: ["control", "banner"],
    note: "Hero: full-width urgency strip replacing the social-proof rating row.",
    title: "Hero: urgentie-banner",
    hypothesis:
      "Een opvallende urgentie-strip bovenaan de hero (in plaats van de reviews-rij) trekt meer aandacht en laat meer bezoekers doorklikken.",
    goal: "cta_click",
    location: "Homepage — hero (bovenste sectie)",
    kind: "presence",
    variantMeta: {
      control: {
        label: "Controle (reviews-rij)",
        description:
          "De huidige hero met de social-proof beoordelingsrij (sterren + aantal reviews).",
      },
      banner: {
        label: "Urgentie-banner",
        description:
          "Verbergt de reviews-rij en toont een volledige-breedte urgentie-strip boven de hero.",
      },
    },
  },
  "reviews-google-snippet": {
    flag: "reviews-google-snippet",
    variants: ["control", "green"],
    note: "Social proof: Google-reviews snippet (default) vs de oude Trustpilot-look groene sterren.",
    title: "Reviews: groene sterren holdback",
    hypothesis:
      "De Google-reviews weergave (Google-logo + gouden sterren) is nu de standaard. We meten of terugvallen op de oude Trustpilot-achtige groene sterrenkaart de doorklik verandert.",
    goal: "cta_click",
    location: "Homepage — social-proof/reviews sectie (onder de hero)",
    kind: "presence",
    variantMeta: {
      control: {
        label: "Controle (Google-snippet)",
        description:
          "De standaard: Google-reviews weergave met het Google-logo, gouden sterren en de 4,8/5 score op basis van Google-reviews. Ook de hero- en footer-sterren zijn goud.",
      },
      green: {
        label: "Groene sterren (oud)",
        description:
          "De oude social-proof kaart: groene sterren en lauwerkrans in de merkkleur, in Trustpilot-stijl.",
      },
    },
  },
} as const satisfies Record<string, Experiment>;

export type ExperimentFlag = keyof typeof experiments;
